import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { NavLink, useNavigation } from 'react-router'
import { TbChevronLeft } from 'react-icons/tb'
import { IconType } from 'react-icons'
import { useBoolean, useClickAnyWhere } from 'usehooks-ts'
import clsx from 'clsx'

import { Hamburger } from './hamburger'
import { Disclosure } from '../disclosure/disclosure'
import { NavCard } from '../../components-v2/navCard/navCard'
import { SquaredIcon } from '~/components-v2/squaredIcon/squaredIcon'
import { Header } from '~/components-v2/header/header'
import { Button, Stack } from '~/components-v2'
import { DrawerArrow } from '~/components-v2/icons'
import { trialText } from '~/config'
import { menuItems, NavItem, Column } from '~/config/navData'

const NavWrapper = ({
  to,
  className,
  children,
  externalLink,
  onClick,
}: {
  to?: string
  className: string
  children: React.ReactNode
  externalLink?: boolean
  onClick?: () => void
}) =>
  to && !externalLink ? (
    <NavLink to={to} className={className} onClick={onClick}>
      {children}
    </NavLink>
  ) : (
    <span className={className + (!externalLink ? ' has-drawer' : '')}>
      {' '}
      {children}
    </span>
  )

const renderIcon = (Icon?: IconType) =>
  Icon ? React.createElement(Icon) : null

const ChildCards = ({ items }: { items: NavItem[] }) => (
  <>
    {items.map((nested) => (
      <React.Fragment key={nested.to ?? nested.label}>
        <NavWrapper
          to={nested.to}
          className="stack nav-card"
          externalLink={nested.externalLink}
        >
          <NavCard
            to={nested.to}
            icon={renderIcon(nested.icon)}
            title={nested.label || ''}
            subTitle={nested.subTitle}
            externalLink={nested.externalLink}
          />
        </NavWrapper>
        {nested.dividerBelow && <hr className="nav-divider" />}
      </React.Fragment>
    ))}
  </>
)

const MasterDetailColumn = ({ column }: { column: Column }) => {
  const [activeLabel, setActiveLabel] = useState<string | undefined>()
  const activeItem = column.items.find((item) => item.label === activeLabel)

  return (
    <div className="nav-sub-column nav-master-detail">
      {column.title && (
        <div className="nav-column-title caps-xxs">{column.title}</div>
      )}
      <div
        className="nav-master-detail-content"
        onMouseLeave={() => setActiveLabel(undefined)}
      >
        <div className="nav-master-list">
          {column.items.map((parent) => (
            <button
              key={parent.label}
              type="button"
              className={clsx(
                'stack nav-card nav-master-item',
                activeLabel === parent.label && 'is-active',
              )}
              onMouseEnter={() => setActiveLabel(parent.label)}
              onFocus={() => setActiveLabel(parent.label)}
            >
              <NavCard
                icon={renderIcon(parent.icon)}
                title={parent.label || ''}
                subTitle={parent.subTitle}
              />
            </button>
          ))}
        </div>
        <div
          className={clsx(
            'nav-master-detail-panel nav-sub-column-content',
            activeItem && 'is-active',
          )}
        >
          {activeItem?.children && <ChildCards items={activeItem.children} />}
        </div>
      </div>
    </div>
  )
}

export const ContentHeader = ({
  mainRef,
}: {
  mainRef: React.RefObject<HTMLDivElement | null>
}) => {
  const { value: hasMenu, toggle, setFalse: closeMenu } = useBoolean()
  const navigation = useNavigation()
  const navRef = useRef<HTMLElement>(null)

  const [activeMenu, setActiveMenu] = useState<'main' | 'sub'>('main')
  const [currentItems, setCurrentItems] = useState<Column[]>([])
  const [currentTitle, setCurrentTitle] = useState('')
  const [openMenu, setOpenMenu] = useState<string | null>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const panelInnerRef = useRef<HTMLDivElement>(null)
  const openMenuRef = useRef<string | null>(openMenu)

  const syncPanelHeight = () => {
    const panel = panelRef.current
    const inner = panelInnerRef.current
    if (!panel || !inner) return
    const panelStyle = getComputedStyle(panel)
    const panelPaddingBlock =
      (parseFloat(panelStyle.paddingTop) || 0) +
      (parseFloat(panelStyle.paddingBottom) || 0)
    const next = openMenuRef.current
      ? `${inner.scrollHeight + panelPaddingBlock}px`
      : '0px'
    if (panel.style.height !== next) panel.style.height = next
  }
  useLayoutEffect(() => {
    const inner = panelInnerRef.current
    if (!inner) return
    const observer = new ResizeObserver(syncPanelHeight)
    observer.observe(inner)
    return () => observer.disconnect()
  }, [])

  useLayoutEffect(() => {
    openMenuRef.current = openMenu
    syncPanelHeight()
  }, [openMenu])

  const handleMobileNavClick = (columns: Column[], title: string) => {
    setCurrentItems(columns)
    setCurrentTitle(title)
    setActiveMenu('sub')
  }

  useEffect(() => {
    const main = mainRef.current

    if (!main) return
  }, [hasMenu, mainRef])

  const [prevHasMenu, setPrevHasMenu] = useState(hasMenu)
  const [prevNavigationState, setPrevNavigationState] = useState(
    navigation.state,
  )
  if (hasMenu !== prevHasMenu || navigation.state !== prevNavigationState) {
    setPrevHasMenu(hasMenu)
    setPrevNavigationState(navigation.state)
    if (hasMenu !== prevHasMenu) setActiveMenu('main')
    if (navigation.state !== 'idle') {
      if (hasMenu) closeMenu()
      setOpenMenu(null)
    }
  }

  useClickAnyWhere((event) => {
    if (!openMenu) return
    const inNav = navRef.current?.contains(event.target as Node)
    const inPanel = panelRef.current?.contains(event.target as Node)
    if (!inNav && !inPanel) setOpenMenu(null)
  })

  const handleMenuMouseLeave = (event: React.MouseEvent) => {
    const to = event.relatedTarget
    if (
      to instanceof Node &&
      (navRef.current?.contains(to) || panelRef.current?.contains(to))
    ) {
      return
    }
    setOpenMenu(null)
  }

  return (
    <Header
      className={clsx(hasMenu && ' is-active')}
      onClickWordmark={closeMenu}
    >
      <Stack align="center" row gap={1} className="header-actions-mobile">
        <Button className="demo" to="/request-demo" variant="secondary">
          Request Demo
        </Button>
        <Button to={trialText.to} variant="inverse">
          {trialText.cta}
        </Button>

        <button
          className="hamburger-menu"
          aria-pressed={hasMenu}
          aria-label="Toggle menu"
          onClick={toggle}
        >
          <Hamburger />
        </button>
      </Stack>

      {/* Desktop Nav */}
      <nav
        className="nav desktop"
        ref={navRef}
        onMouseLeave={handleMenuMouseLeave}
      >
        <div className="nav-row">
          <Stack gap={7} row grow={1} className="header-menu">
            {menuItems.map((item) => {
              if (item.columns) {
                return (
                  <button
                    key={item.title}
                    className={clsx(
                      'nav-item',
                      openMenu === item.title && 'is-open',
                    )}
                    onMouseEnter={() => setOpenMenu(item.title!)}
                    onFocus={() => setOpenMenu(item.title!)}
                  >
                    <span className="disclosure-label nav-m">{item.title}</span>
                  </button>
                )
              }
              if (item.externalLink && item.to) {
                return (
                  <a
                    className="nav-item"
                    key={item.to}
                    href={item.to}
                    target="_blank"
                    rel="noreferrer"
                    onMouseEnter={() => setOpenMenu(null)}
                  >
                    {item.title}
                  </a>
                )
              }
              return (
                <NavLink
                  className="nav-item"
                  key={item.to}
                  to={item.to!}
                  onMouseEnter={() => setOpenMenu(null)}
                >
                  {item.title}
                </NavLink>
              )
            })}
          </Stack>
          <Stack row gap={2} className="header-actions">
            <Button to="/request-demo" variant="secondary">
              Request Demo
            </Button>
            <Button to={trialText.to} variant="inverse">
              {trialText.cta}
            </Button>
          </Stack>
        </div>
      </nav>

      <div
        className={clsx('nav-panel', openMenu && 'is-open')}
        ref={panelRef}
        onMouseLeave={handleMenuMouseLeave}
      >
        <div className="nav-panel-inner" ref={panelInnerRef}>
          {menuItems.map((item) => {
            if (!item.columns || openMenu !== item.title) return null
            return (
              <Stack
                key={item.title}
                className={clsx(
                  'mega-card',
                  `mega-card--${item.title.toLowerCase().replace(/\s+/g, '-')}`,
                )}
                row
                gap={6}
              >
                {item.columns.map((column, colIndex) => {
                  if (column.hasChildren) {
                    return (
                      <MasterDetailColumn
                        key={colIndex.toString()}
                        column={column}
                      />
                    )
                  }
                  return (
                    <div className="nav-sub-column" key={colIndex.toString()}>
                      {column.title && (
                        <div className="nav-column-title caps-xxs">
                          {column.title}
                        </div>
                      )}
                      <div className="nav-sub-column-content">
                        {column.items.map((child) => (
                          <React.Fragment key={child.label}>
                            <NavWrapper
                              to={child.to}
                              className="stack nav-card"
                              externalLink={child.externalLink}
                            >
                              <NavCard
                                to={child.to}
                                icon={renderIcon(child.icon)}
                                title={child.label || ''}
                                subTitle={child.subTitle}
                                externalLink={child.externalLink}
                              >
                                {child.children && (
                                  <Stack
                                    gap={2}
                                    className="nav-sub-column drawer"
                                  >
                                    {child.children.map((nested) =>
                                      nested.externalLink ? (
                                        <React.Fragment key={nested.label}>
                                          <NavCard
                                            icon={renderIcon(nested.icon)}
                                            title={nested.label || ''}
                                            subTitle={nested.subTitle}
                                            externalLink={true}
                                            to={nested.to}
                                          />
                                          {nested.dividerBelow && (
                                            <hr className="nav-divider" />
                                          )}
                                        </React.Fragment>
                                      ) : (
                                        nested.to && (
                                          <React.Fragment key={nested.to}>
                                            <NavLink to={nested.to}>
                                              <NavCard
                                                icon={renderIcon(nested.icon)}
                                                title={nested.label || ''}
                                                subTitle={nested.subTitle}
                                                to={nested.to}
                                                externalLink={
                                                  nested.externalLink
                                                }
                                              />
                                            </NavLink>
                                            {nested.dividerBelow && (
                                              <hr className="nav-divider" />
                                            )}
                                          </React.Fragment>
                                        )
                                      ),
                                    )}
                                  </Stack>
                                )}
                              </NavCard>
                            </NavWrapper>
                            {child.dividerBelow && (
                              <hr className="nav-divider" />
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </Stack>
            )
          })}
        </div>
      </div>

      {/* Mobile Nav */}
      <nav className="nav mobile">
        <Stack
          className={clsx(
            'header-menu mobile-panel',
            activeMenu === 'main' && 'active',
          )}
        >
          {menuItems.map((item) =>
            item.columns ? (
              <a
                className="stack nav-card"
                key={item.title}
                onClick={(e) => {
                  e.preventDefault()
                  handleMobileNavClick(item.columns ?? [], item.title)
                }}
              >
                <span>{item.title}</span>
                <DrawerArrow className="nav-drawer-arrow" />
              </a>
            ) : item.to && item.externalLink ? (
              <a
                className="stack nav-card"
                key={item.to}
                href={item.to}
                target="_blank"
                rel="noreferrer"
              >
                <span>{item.title}</span>
                <DrawerArrow className="nav-drawer-arrow" />
              </a>
            ) : item.to ? (
              <NavLink className="stack nav-card" key={item.to} to={item.to}>
                <span>{item.title}</span>
                <DrawerArrow className="nav-drawer-arrow" />
              </NavLink>
            ) : null,
          )}
        </Stack>

        <Stack
          className={`header-menu mobile-panel-secondary ${activeMenu === 'sub' ? 'active' : ''}`}
        >
          <Stack className="mobile-panel-secondary-container">
            <h3
              className="mobile-panel-secondary-title"
              role="button"
              tabIndex={0}
              aria-label={`Back from ${currentTitle}`}
              onClick={() => setActiveMenu('main')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  setActiveMenu('main')
                }
              }}
            >
              <span className="touch-back-arrow" aria-hidden="true">
                <TbChevronLeft />
              </span>
              {currentTitle}
            </h3>
            <Stack className="mobile-nav-container" row wrap={'wrap'} gap={4}>
              {currentItems.map((column) => {
                const itemsWithChildren = column.items.filter(
                  (item: NavItem) => item.children,
                )
                const itemsWithoutChildren = column.items.filter(
                  (item: NavItem) => !item.children,
                )

                return (
                  <React.Fragment key={column.items[0]?.label}>
                    {column.title && (
                      <div className="nav-column-title caps-xxs">
                        {column.title}
                      </div>
                    )}
                    {itemsWithoutChildren.length > 0 && (
                      <Stack className="mobile-nav-subcolumn" gap={1}>
                        {itemsWithoutChildren.map((item: NavItem) => (
                          <NavWrapper
                            key={item.label}
                            to={item.to}
                            className="stack nav-card"
                            externalLink={item.externalLink}
                            onClick={() => {
                              closeMenu()
                              setActiveMenu('main')
                            }}
                          >
                            <NavCard
                              to={item.to}
                              icon={renderIcon(item.icon)}
                              title={item.label || ''}
                              subTitle={item.subTitle}
                              externalLink={item.externalLink}
                            />
                          </NavWrapper>
                        ))}
                      </Stack>
                    )}

                    {itemsWithChildren.map((item: NavItem) => (
                      <Stack
                        key={item.label}
                        className="mobile-nav-subcolumn"
                        gap={2}
                      >
                        <Disclosure
                          title={
                            <>
                              {item.icon && (
                                <SquaredIcon icon={renderIcon(item.icon)!} />
                              )}
                              <span>{item.label}</span>
                            </>
                          }
                        >
                          <Stack className="mobile-subitems" gap={1}>
                            {item.children?.map((child) => (
                              <NavWrapper
                                key={child.to ?? child.label}
                                to={child.to}
                                className="stack nav-card"
                                externalLink={child.externalLink}
                                onClick={() => {
                                  closeMenu()
                                  setActiveMenu('main')
                                }}
                              >
                                <NavCard
                                  to={child.to}
                                  icon={renderIcon(child.icon)}
                                  title={child.label || ''}
                                  subTitle={child.subTitle}
                                  externalLink={child.externalLink}
                                />
                              </NavWrapper>
                            ))}
                          </Stack>
                        </Disclosure>
                      </Stack>
                    ))}
                  </React.Fragment>
                )
              })}
            </Stack>
          </Stack>
        </Stack>
      </nav>
    </Header>
  )
}
